/**
 * Edit Device Type Form Composer
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  EditDeviceTypeFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditDeviceTypeFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { renderTextControl,
  renderSelectControl, renderButtonControl, type ControlConfig } from '../../formRenderFunctions'
import { useSelectOptions } from '@/hooks/useSelectOptions'
import { coerceFieldValue } from '@/lib/utils'
import { AppLogger } from '@/lib/logger'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Tag } from 'lucide-react'

const log = AppLogger.getLogger(import.meta)

export type EditDeviceTypeComposerProps = {
  deviceTypeNumber: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues: Partial<EditDeviceTypeFormData>
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof EditDeviceTypeFormData, value: unknown): string | null => {
  const shape = EditDeviceTypeFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function EditDeviceTypeComposer(props: EditDeviceTypeComposerProps) {
  const { deviceTypeNumber, onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  // Fetch manufacturers - single call, auto-transformed to options
  const { options: manufacturerOptions, isLoading: loadingManufacturers } = useSelectOptions({
    endpoint: 'getDeviceManufacturers',
    dataKey: 'manufacturers',
    valueField: 'manufacturerNumber',
    labelField: 'manufacturerName',
  })

  const updateDeviceType = useApiMutation('upsertDeviceType', 'POST', {
    invalidates: ['getDeviceTypes'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to update device type:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: EditDeviceTypeFormSchema,
    defaultValues: {
      deviceTypeNumber: deviceTypeNumber,
      deviceTypeName: initialValues?.deviceTypeName ?? '',
      deviceTypeModel: initialValues?.deviceTypeModel ?? '',
      deviceTypeManufacturerNumber: initialValues?.deviceTypeManufacturerNumber ?? ('' as any),
      deviceTypeCategory: initialValues?.deviceTypeCategory ?? ('' as any),
      deviceTypeStatus: initialValues?.deviceTypeStatus ?? ('' as any),
    },
    onSubmit: async ({ value }) => {
      const formValue = value as EditDeviceTypeFormData
      const coerced = {
        deviceTypeNumber: deviceTypeNumber,
        deviceTypeName: formValue.deviceTypeName?.trim() ?? '',
        deviceTypeModel: formValue.deviceTypeModel?.trim() ?? '',
        deviceTypeManufacturerNumber: formValue.deviceTypeManufacturerNumber
          ? Number(formValue.deviceTypeManufacturerNumber)
          : undefined,
        deviceTypeCategory: formValue.deviceTypeCategory || undefined,
        deviceTypeStatus: formValue.deviceTypeStatus || undefined,
      }

      const parsed = EditDeviceTypeFormSchema.safeParse(coerced)

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as keyof EditDeviceTypeFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      const apiRequest = transformToApiRequest(parsed.data)
      await updateDeviceType.mutateAsync(apiRequest)
    },
  })

  const renderTextControlField = (control: any) => {
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof EditDeviceTypeFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: ['deviceTypeName', 'deviceTypeManufacturerNumber'].includes(control.name),
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  const renderSelectControlField = (control: any) => {
    // Get options based on field name
    const getOptionsForField = () => {
      if (control.name === 'deviceTypeManufacturerNumber') {
        return { options: manufacturerOptions, isLoading: loadingManufacturers }
      }
      // Static options from config
      return {
        options: FormConfigLoader.normalizeSelectOptions(control.selectOptions),
        isLoading: false
      }
    }

    const { options, isLoading } = getOptionsForField()

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof EditDeviceTypeFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
        listeners={
          control.name === 'deviceTypeManufacturerNumber'
            ? {
                // The manufacturer select stores its value as a string, but the schema
                // requires a number. Coerce the stored value so the form-level validation
                // (z.number()) passes and the Save button re-enables after changing it.
                onChange: ({ value }) => {
                  if (typeof value === 'string' && value !== '') {
                    form.setFieldValue('deviceTypeManufacturerNumber', Number(value) as any)
                  }
                },
              }
            : undefined
        }
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: ['deviceTypeManufacturerNumber', 'deviceTypeCategory', 'deviceTypeStatus'].includes(control.name),
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            options: options,
            isLoading: isLoading,
          }
          return renderSelectControl(config, bound)
        }}
      </form.AppField>
    )
  }

  const renderControl = (control: any) => {
    if (control.htmlControl === 'select') {
      return renderSelectControlField(control)
    }
    return renderTextControlField(control)
  }

  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null
      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  const formBody = (
    <>
        {renderFormContent()}
        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: updateDeviceType.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Updating...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Update'),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Tag className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

export {
  EditDeviceTypeFormSchema,
  transformToApiRequest,
  DeviceTypeCategoryEnum,
  DeviceTypeStatusEnum,
  FORM_CONFIG,
  type EditDeviceTypeFormData,
  type DeviceTypeCategory,
  type DeviceTypeStatus,
} from './schema'
